import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs';
import {AppModalType} from '../../types/modal/app-modal.type';
import {AppModalCloseListener} from '../../types/modal/app-modal-close-listener.type';
import {AppBaseModal} from '../../models/modal/app-base-modal.model';
import {AppModalTypeEnum} from '../../enums/modal/app-modal-type.enum';

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    private readonly openModals: WritableSignal<Array<AppModalType>> = signal([]);
    private readonly router: Router = inject(Router);

    public constructor() {
        this.router.events.pipe(
                filter(event => event instanceof NavigationStart),
                takeUntilDestroyed()
            )
            .subscribe({
                next: () => this.closeAllModalsOnNavigation(),
                error: (error: unknown) => console.error('Failed to observe the navigation for closing the modals.', error)
            });
    }

    /**
     * All currently open modals, in the order they were added. Rendered by the `ModalsComponent` — components normally do not need this.
     */
    public getOpenModalsSignal(): Signal<Array<AppModalType>> {
        return this.openModals.asReadonly();
    }

    /**
     * @param id the modal id generated by {@link generateModalId}.
     * @returns the open modal with that id, or `undefined` once it has been closed.
     */
    public getOpenModalById(id: string): AppModalType | undefined {
        return this.openModals()
            .find(modal => modal.id === id);
    }

    /**
     * Builds a modal object and wires up its `afterClosed()` / `notifyClosed()` close handling. The modal is NOT displayed yet —
     * pass it to {@link addModal} (or {@link replaceAllByModalType}) afterwards.
     *
     * Prefer the specialised services (`DialogService`, `SnackbarService`) over calling this directly; use it only for a new modal kind.
     *
     * The type parameter `O` decides what the returned object is: pass the concrete modal model of the kind you are building
     * (`AppSnackbarModal`, `AppDialogModal<Component, Data, Result>`, `AppGenericModal`), and `modal` is checked against it. Without a
     * type parameter you get the union of all modal kinds, which is rarely what you want.
     *
     * ```ts
     * // A dialog rendering MyDialogComponent, handing back a boolean when it closes.
     * const modal = this.modalService.createModal<AppDialogModal<MyDialogComponent, MyDialogData, boolean>>({
     *     component: MyDialogComponent,
     *     modalData: { documentId },
     *     modalType: AppModalTypeEnum.Dialog,
     *     verticalPosition: AppModalVerticalPositionEnum.Middle,
     *     horizontalPosition: AppModalHorizontalPositionEnum.Center,
     *     isModal: true,
     *     hasBackdrop: true,
     *     isShowing: signal(true),
     *     qaTag: 'my-dialog'
     * });
     * modal.afterClosed(isConfirmed => ...);
     * this.modalService.addModal(modal);
     * ```
     *
     * @param modal the fields to set; everything not passed falls back to the non-modal, backdrop-less defaults (`isShowing` starts `false`,
     *                so set `isShowing: signal(true)` to show it immediately). `component`, `modalData`, `modalType` and both positions
     *                have no default and must always be given.
     * @returns the ready-to-use modal, with an id of its own and `afterClosed()` / `notifyClosed()` already implemented.
     */
    public createModal<O extends AppBaseModal = AppModalType>(modal: Partial<O>): O {
        const id = this.generateModalId();
        const closeListeners: Array<AppModalCloseListener<unknown>> = [];
        let hasClosed = false;
        let closeReturnData: unknown = undefined;
        return {
            id,
            isModal: false,
            hasBackdrop: false,
            isShowing: signal(false),
            shouldAutoFocus: true,
            shouldCloseOnBackdropClick: true,
            shouldCloseOnEscapeKey: true,
            shouldCloseOnNavigation: false,
            qaTag: 'generic-modal',
            ...modal,
            afterClosed: (listener: AppModalCloseListener<unknown>): (() => void) => {
                if (hasClosed) {
                    listener(closeReturnData);
                    return () => undefined;
                }
                closeListeners.push(listener);
                return () => {
                    const listenerIndex = closeListeners.indexOf(listener);
                    if (listenerIndex !== -1) {
                        closeListeners.splice(listenerIndex, 1);
                    }
                };
            },
            notifyClosed: (returnData?: unknown): void => {
                if (hasClosed) {
                    return;
                }
                hasClosed = true;
                closeReturnData = returnData;
                for (const listener of [...closeListeners]) {
                    try {
                        listener(returnData);
                    } catch (error) {
                        console.error('A close listener of modal ' + id + ' failed.', error);
                    }
                }
                closeListeners.length = 0;
            }
        } as O;
    }

    /**
     * Closes every modal of the same type before showing the given one — used for modal kinds that may only exist once at a time,
     * such as snackbars.
     *
     * @param modal the modal to show; its `modalType` decides which ones are closed first. Modals of other types stay open.
     */
    public replaceAllByModalType(modal: AppModalType): void {
        this.closeAllModalsByType(modal.modalType);
        this.addModal(modal);
    }

    /**
     * Displays a modal created via {@link createModal}. Stacks on top of the modals that are already open.
     *
     * @param modal must come from {@link createModal} — otherwise it has no close handling and can never be closed properly.
     */
    public addModal(modal: AppModalType): void {
        this.openModals.update(modals => [
            ...modals,
            modal
        ]);
    }

    /**
     * Closes a modal and notifies its `afterClosed()` listeners. Safe to call more than once — the listeners only ever run for the first call.
     *
     * @param modal the modal to close; one that is no longer open still notifies its listeners.
     * @param returnData the result handed to the `afterClosed()` listeners; leaving it out means "closed without a result" (e.g. a cancelled dialog).
     */
    public closeModal<ReturnData>(modal: AppBaseModal<unknown, ReturnData>, returnData?: ReturnData): void {
        modal.notifyClosed(returnData);

        if (!this.getOpenModalById(modal.id)) {
            return;
        }

        this.openModals.update(modals => {
            return modals.filter(openModal => openModal.id != modal.id);
        });
    }

    /**
     * Closes every open modal without a return value — pending `showConfirmDialog()`/`showInputDialog()` promises therefore resolve
     * as if cancelled. Meant for global resets such as a logout or a server switch.
     */
    public closeAllModals(): void {
        for (const modal of this.openModals()) {
            this.closeModal(modal);
        }
    }

    /**
     * Closes all open modals of one kind (e.g. every snackbar) without a return value, leaving the other kinds untouched.
     *
     * @param modalType the kind to close.
     */
    public closeAllModalsByType(modalType: AppModalTypeEnum): void {
        for (const modal of this.openModals()) {
            if (modal.modalType === modalType) {
                this.closeModal(modal);
            }
        }
    }

    /**
     * @returns an id that no currently open modal uses. {@link createModal} already calls this, so it is only needed when building an
     *          modal object by hand.
     */
    public generateModalId(): string {
        let id = 'modal-' + (Date.now() + Math.random()).toString(36);
        if (this.openModals()
            .map(modal => modal.id)
            .includes(id)) {
            id = this.generateModalId();
        }
        return id;
    }

    private closeAllModalsOnNavigation(): void {
        for (const modal of this.openModals()) {
            if (modal.shouldCloseOnNavigation) {
                this.closeModal(modal);
            }
        }
    }
}
